import { useEffect, useRef } from 'react';
import { isEffectivelyOnline, subscribeConnectivity } from '../offline/connectivity';

const DEFAULT_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
const COOLDOWN_MS = 5000; // 5 seconds between polls to avoid double triggers

/**
 * Auto-refreshes the current route's data at a configurable interval.
 * Calls refetchFn (which should use fetchPolicy: 'network-only') to get fresh data.
 * Pauses when the tab is hidden or when offline.
 * Triggers an immediate refresh when the tab becomes visible or connectivity returns.
 * Has a 5s cooldown to prevent double triggers (e.g., visibility + timer coinciding).
 */
export function usePollingRevalidation(
  refetchFn: () => void | Promise<void>,
  intervalMs = DEFAULT_INTERVAL_MS
) {
  const refetchRef = useRef(refetchFn);
  refetchRef.current = refetchFn;
  const lastPollRef = useRef(0);

  useEffect(() => {
    const poll = () => {
      const now = Date.now();
      if (now - lastPollRef.current < COOLDOWN_MS) return;

      // Con el modo «sin servidor» no se refresca: Apollo convertiría el
      // `network-only` en caché, se gastaría el enfriamiento y, al volver el
      // servidor un instante después, el refresco bueno no saldría (spec
      // 2026-10-09 § 9). El que vale es el de la transición de abajo.
      if (document.visibilityState === 'visible' && isEffectivelyOnline()) {
        lastPollRef.current = now;
        refetchRef.current();
      }
    };

    const intervalId = setInterval(poll, intervalMs);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') poll();
    };

    const handleOnline = () => poll();

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnline);
    // Vuelve el servidor (sale el modo «sin servidor»): la pantalla se pidió
    // contra la caché y nada más la refrescaba hasta el intervalo (5 min).
    const unsubscribeConnectivity = subscribeConnectivity((online) => {
      if (online) poll();
    });

    return () => {
      unsubscribeConnectivity();
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
    };
  }, [intervalMs]);
}
